<template>
  <div>

    <va-modal
      ref="licenseModal"
      class="evollu-form"
      :value="showLicenseModal"
      :okText="$t('licenses.save')"
      :okToClose="false"
      @ok="submit"
      :cancelText="$t('licenses.back')"
      @cancel="close"
      :showDelete="isUpdate && !isRenew"
      :dangerText="$t('modals.buttons.delete')"
      @remove="prepareForDestroy"
      >

      <div slot="header" class="d-flex justify-content-between w-100">
        <h4 class="mb-0">
          <b>{{ isRenew ? $t('licenses.renew_license') : isUpdate ? $t('licenses.update_license') : $t('licenses.create_license') }}</b>
        </h4>
      </div>

      <div class="form-section">

        <label class="section-label form-label mt-4">{{ $t('licenses.license_info') }}</label>

        <div class="row">

          <div class="form-group col-12 col-md-6">
            <div class="input-group">
              <va-input
                :placeholder="$t('licenses.name')"
                v-model="form.data.name"
                maxLength="75"
                id="license-name"
                :error="form.errors.has('name')"
                :error-messages="[$t(`${form.errors.get('name')}`)]"
                @input="form.errors.clear('name')"
                required/>
            </div>
          </div>

          <div class="form-group col-12 col-md-6">
            <div class="input-group datepicker">
              <RangeDatePicker
                class="w-100"
                id="date-picker-range"
                v-model="form.data.dateRange"
                :placeholder="$t('licenses.date_interval')"
                @input="form.errors.clear('start_date'); form.errors.clear('end_date')"
                @on-change="form.errors.clear('start_date'); form.errors.clear('end_date')" />
              <small v-show="form.errors.has('start_date') || form.errors.has('end_date')"  class="help text-danger">
                {{ $t(form.errors.get('start_date')) }}
              </small>
            </div>
          </div>

        </div>

        <div class="row">

          <div class="form-group col-12 col-md-6">
            <div class="input-group">
              <multiselect
                id="license-types-multiselect"
                v-model="form.data.license_type"
                :options="licenseTypes"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                open-direction="bellow"
                :placeholder="$t('licenses.license_type')"
                @select="form.errors.clear('license_type_id')"
                :allow-empty="false"
                :multiple="false"
                >
                  <template slot="option" slot-scope="props">
                    {{ $t(props.option.name) }}
                  </template>
                  <template slot="singleLabel" slot-scope="option">
                    {{ $t(option.option.name) }}
                  </template>
              </multiselect>
              <small v-show="form.errors.has('license_type_id')"  class="help text-danger">
                {{ $t(form.errors.get('license_type_id')) }}
              </small>
            </div>
          </div>

          <div class="form-group col-12 col-md-6">
            <div class="input-group">
              <multiselect
                id="license-entity-multiselect"
                v-model="form.data.entity"
                :options="entityOptions"
                track-by="id"
                label="name"
                :placeholder="$t('licenses.entity')"
                select-label=""
                deselect-label="×"
                open-direction="bellow"
                @select="form.errors.clear('entity_id')"
                :allow-empty="false"
                :multiple="false"
                :disabled="isUpdate"
                >
                  <template slot="option" slot-scope="props">
                    {{ formatedEntityLabel(props.option) }}
                  </template>
                  <template slot="singleLabel" slot-scope="props">
                    {{ formatedEntityLabel(props.option) }}
                  </template>
              </multiselect>
              <small v-show="form.errors.has('entity_id')"  class="help text-danger">
                {{ $t(form.errors.get('entity_id')) }}
              </small>
            </div>
          </div>

        </div>

        <div class="row">
          <div class="form-group col-12 col-md-12">
            <div class="input-group">
              <multiselect
                id="license-kiosks-multiselect"
                v-model="form.data.kiosk"
                :options="kioskOptions"
                track-by="id"
                label="name"
                :placeholder="$t('licenses.kiosk')"
                deselect-label="×"
                open-direction="bellow"
                @select="form.errors.clear('kiosk_id')"
                :allow-empty="false"
                :multiple="false"
                :disabled="isUpdate || !isUpdate && kiosk !== undefined"
                >
                <template slot="option" slot-scope="props">
                  {{ `${props.option.name || ''} (${props.option.serial})` }}
                </template>
                <template slot="singleLabel" slot-scope="props">
                  {{ `${props.option.name} (${props.option.serial})` }}
                </template>
              </multiselect>
              <small v-show="form.errors.has('kiosk_id')"  class="help text-danger">
                {{ $t(form.errors.get('kiosk_id')) }}
              </small>
            </div>
          </div>
        </div>

      </div>

      <div class="form-section">

        <label class="form-label section-label">{{$t('licenses.invoice')}}</label>

        <div class="row">

          <div class="form-group col-12 col-md-6">
            <div class="input-group">
              <va-input
                :placeholder="$t('licenses.invoice_number')"
                v-model="form.data.invoice_number"
                maxLength="75"
                id="invoice-number"
                :error="form.errors.has('invoice_number')"
                :error-messages="[$t(`${form.errors.get('invoice_number')}`)]"
                @input="form.errors.clear('invoice_number')"
                required/>
            </div>
          </div>

          <div class="form-group col-12 col-md-6">
            <div class="input-group datepicker">
              <DatePicker
                id="date-picker-range-invoice-date"
                class="w-100"
                :placeholder="$t('licenses.invoice_date')"
                v-model="form.data.invoice_date"
                @input="form.errors.clear('invoice_date')" />
              <small v-show="form.errors.has('invoice_date')"  class="help text-danger">
                {{ $t(form.errors.get('invoice_date')) }}
              </small>
            </div>
          </div>
        </div>

        <div class="row" :class="{'mt-4':  !form.errors.has('invoice_number') && !form.errors.has('invoice_date') }">
          <div class="form-group with-icon-right col-md-6">
            <div class="input-group">
              <va-input
                type="number"
                step="0.01"
                :placeholder="$t('licenses.invoice_amount')"
                v-model="form.data.invoice_amount"
                maxLength="75"
                id="invoice-number"
                :error="form.errors.has('invoice_amount')"
                :error-messages="[$t(`${form.errors.get('invoice_amount')}`)]"
                @input="form.errors.clear('invoice_amount')"
                required/>
            </div>
          </div>
        </div>

      </div>
    </va-modal>

    <ConfirmationModal
      v-if="isDestroyingLicense"
      :title="$t('licenses.delete_license')"
      :body="$t('licenses.delete_modal_body', {name: license.name})"
      @modal-confirmed="destroyLicense"
      @modal-canceled="cancelDestroyLicense" />

  </div>

</template>

<script>
import DatePicker from '@/components/common/DatePicker'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import Errors from '@/utils/StandardErrors.js'
import { KiosksService, LicensesService } from '@/api/LeadsService'
import Multiselect from 'vue-multiselect'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import ConfirmationModal from '@/components/common/ConfirmationModal'
const licenseInfoType = 300
const readWriteAcessType = 2

export default {
  props: {
    license: {
      type: Object,
      required: false,
    },
    kiosk: {
      type: Object,
      required: false
    },
    isRenew: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    RangeDatePicker,
    DatePicker,
    Multiselect,
    ConfirmationModal
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  mounted: function () {
    this.showLicenseModal = true

    if (this.isUpdate) {
      this.prepareForUpdate()
    } else {
      this.getUnlicensedKiosks().then(kiosks => {
        this.kioskOptions = kiosks
        if (this.kiosk) {
          this.form.data.kiosk = this.kioskOptions.find(kiosk => kiosk.id === this.kiosk.id)
        }
      })
    }
  },
  computed: {
    leadsService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollead_server)
    },
    licensePermissions: function () {
      return this.leadsService.user_permissions.filter(permission => permission.access_type === readWriteAcessType && permission.info_type === licenseInfoType)
    },
    entityOptions: function () {
      if (this.licensePermissions.some(permission => permission.entity_id === null)) {
        return this.leadsService.entities
      } else {
        return this.leadsService.entities.filter(entity => this.licensePermissions.some(permission => permission.entity_id === entity.id))
      }
    },
    isUpdate: function () {
      return this.license !== undefined && this.license !== null
    }
  },
  data: function () {
    return {
      showLicenseModal: false,
      isDestroyingLicense: false,
      kioskOptions: [],
      licenseTypes: [
        { id: 1, name: 'licenses.types.standard' },
        { id: 2, name: 'licenses.types.premium' }
      ],
      form: {
        data: {
          name: null,
          entity: null,
          dateRange: {
            start: null,
            end: null
          },
          license_type: null,
          start_date: null,
          end_date: null,
          invoice_date: null,
          invoice_amount: null,
          invoice_number: null,
          kiosk: null
        },
        errors: new Errors()
      }
    }
  },
  methods: {
    getUnlicensedKiosks: function () {
      return new Promise((resolve, reject) => {
        KiosksService.unlicensed()
          .then(kiosks => resolve(kiosks))
          .catch(errors => {
            this.showToast(this.$t('licenses.unable_to_get_kiosks'), this.$store.state.errorToastOptions)
            resolve([])
          })
      })
    },
    submit: function () {
      let data = this.getFormData()
      let request = this.isRenew ? LicensesService.renew(this.license.id, data) : this.isUpdate ? LicensesService.update(this.license.id, data) : LicensesService.store(data)

      request
        .then(response => {
          this.showToast(this.isRenew ? this.$t('licenses.license_renewed') : this.isUpdate ? this.$t('licenses.license_updated') : this.$t('licenses.license_created'), this.$store.state.successToastOptions)
          this.$emit(this.isRenew ? 'renewed' : this.isUpdate ? 'updated' : 'created', response.data.data)
          this.showLicenseModal = false
        })
        .catch(errors => {
          if (errors.response.status === 422) {
            this.form.errors.record(errors.response.data.errors, 'licenses.errors.')
            this.showToast(this.$t('licenses.form_contains_errors'), this.$store.state.errorToastOptions)
          } else if (errors.response.status === 403) {
            this.showToast(this.$t('licenses.missing_create_license_permissions', this.$store.state.errorToastOptions))
          } else {
            this.showToast(this.$t('licenses.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
    },
    getFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form.data))

      if (data.dateRange) {
        data.start_date = data.dateRange.start
        data.end_date = data.dateRange.end
        delete data.dateRange
      }

      if (data.entity) {
        data.entity_id = data.entity.id
        delete data.entity
      }

      if (data.license_type) {
        data.license_type_id = data.license_type.id
        delete data.license_type
      }

      if (data.kiosk) {
        data.kiosk_id = data.kiosk.id
        delete data.kiosk
      }

      return data
    },
    close: function () {
      if (!this.isDestroyingLicense) {
        setTimeout(() => {
          this.$emit('modalClosed')
        }, 450)
      }
    },
    prepareForUpdate: function () {
      let formData = JSON.parse(JSON.stringify(this.license))
      this.form.data = Object.assign(formData, {
        license_type: this.licenseTypes.find(type => type.id === formData.license_type_id),
        dateRange: {
          start: formData.start_date,
          end: formData.end_date
        }
      })
    },
    prepareForDestroy: function () {
      this.isDestroyingLicense = true
      this.showLicenseModal = false
    },
    destroyLicense: function () {
      this.isDestroyingLicense = false
      LicensesService.destroy(this.license.id)
        .then(response => {
          this.isDestroyingLicense = false
          this.showLicenseModal = false
          this.showToast(this.$t('licenses.license_deleted'), this.$store.state.successToastOptions)
          this.$nextTick(() => this.$emit('destroyed'))
        })
        .catch(errors => {
          this.showLicenseModal = true
          this.showToast(this.$t('licenses.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    cancelDestroyLicense: function () {
      this.isDestroyingLicense = false
      this.showLicenseModal = true
    }
  },
  watch: {
    'form.data.kiosk': function (newValue) {
      if (newValue && !this.isUpdate) {
        this.form.data.name = this.$t('licenses.license_name', { name: newValue.name })
      }
    }
  }
}
</script>
